/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactElement, ReactNode, Suspense } from 'react';

import { validateImage } from '@lib';
import { getChannel, getContent, getPlaceId } from '@lib/server';
import { IconStarFilled, IconToolsKitchenOff } from '@tabler/icons-react';
import { APIImageType, RTEContent, SEO, SelectOption } from '@types';
import clsx from 'clsx';

import { Badge, Button, RichText, SafeImage, Text } from '../..';
import { EmptyState } from '../empty-state';
import { BlockBase } from '../utils';
import { MenusClient } from './client';

interface MenuItem {
  name: string;
  description?: string;
  price?: number;
  thumbnail?: Array<APIImageType>;
  dietaryInformation?: Array<SelectOption>;
  calories?: number;
  additionalInformation?: RTEContent;
  highlight?: string;
}

interface MenuSection {
  name?: string;
  description?: RTEContent;
  image?: Array<APIImageType>;
  visibility?: boolean;
  items?: Array<MenuItem>;
}

export interface Menu {
  menuName: string;
  slug: string;
  shortDescription: RTEContent;
  pdf?: Array<{ url: string }>;
  featuredImage?: Array<APIImageType>;
  sections?: Array<MenuSection>;
  seo?: SEO;
}

export interface MenuProps {
  menu: Menu;
  title?: string;
  className?: string;
  options?: object;
  children?: ReactNode;
  renderMenuItem?: (item: MenuItem) => ReactElement<any>;
  renderMenuSection?: (
    section: MenuSection,
    renderMenuItem: (item: MenuItem) => ReactElement<any>,
  ) => ReactElement<any>;
}

interface GetMenusProps {
  placeId: string;
}

async function getMenus({ placeId }: GetMenusProps) {
  try {
    const fetchedMenus = getContent<Menu>('menu')
      .setTags(['menu'])
      .setDepth(0)
      .setWhere([['sys.referencedBy', 'contains', placeId]]);

    const menus = await fetchedMenus.fetch();

    return menus || [];
  } catch (error) {
    console.error('Error fetching non-repeating events:', error);
    throw new Error('Unable to retrieve non-repeating events');
  }
}

export async function Menus({ id, params }: BlockBase) {
  const channel = await getChannel(decodeURIComponent(params.domain));

  if (!channel?.channelId) {
    return null;
  }

  const { placeId } = await getPlaceId(channel, decodeURIComponent(params.venue || ''));

  if (!placeId) {
    return null;
  }

  const menus = await getMenus({
    placeId: placeId,
  });

  if (!menus.length) {
    return <EmptyState icon={IconToolsKitchenOff} contentName="menus" />;
  }

  return (
    <Suspense>
      <MenusClient menus={menus} id={id} />
    </Suspense>
  );
}

export const MenuComponent: FC<MenuProps> = ({
  menu,
  title = 'Menu',
  className,
  renderMenuItem = defaultMenuItemRenderer,
  renderMenuSection = defaultMenuSectionRenderer,
  children,
}) => {
  if (!menu) {
    return <EmptyState icon={IconToolsKitchenOff} contentName="menus" />;
  }

  return (
    <div className={clsx('flex flex-col gap-2', className)}>
      <Text element="h2">{menu.menuName || title}</Text>

      {menu.shortDescription?.content && <RichText>{menu.shortDescription?.content}</RichText>}

      {menu.pdf && (
        <div className="mt-1">
          <Button href={menu.pdf[0].url} target="_BLANK">
            Download Menu
          </Button>
        </div>
      )}

      {children}

      {menu.sections?.map((section) => renderMenuSection(section, renderMenuItem))}
    </div>
  );
};

const defaultMenuSectionRenderer = (
  { visibility = true, ...section }: MenuSection,
  renderMenuItem: (item: MenuItem) => ReactElement<any>,
) => {
  if (!visibility) {
    return null;
  }

  return (
    <div key={section.name} className="flex flex-col gap-2 py-4">
      {section.name && <Text element="h3">{section.name}</Text>}
      {section.description?.content && <RichText>{section.description.content}</RichText>}

      <div className="mt-6 grid grid-cols-1 gap-8 gap-y-6 md:grid-cols-2">{section.items?.map(renderMenuItem)}</div>
    </div>
  );
};

const defaultMenuItemRenderer = (menuItem: MenuItem) => {
  return (
    <div key={menuItem.name} className="flex w-full flex-col overflow-hidden rounded-lg sm:flex-row">
      {validateImage(menuItem.thumbnail) && (
        <div className="relative min-h-40 w-full shrink-0 sm:w-40">
          <SafeImage src={menuItem.thumbnail} alt={menuItem.name} fill className="inset-0 object-cover" />
        </div>
      )}
      <div className="h-min grow">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <div className="inline text-balance">
            {menuItem.highlight && (
              <Badge className="mr-3 -translate-y-0.5 gap-1">
                <IconStarFilled className="size-3" />
                {menuItem.highlight}
              </Badge>
            )}
            <Text element="span" elementStyle="h4" className="inline">
              {menuItem.name}
            </Text>
          </div>
          {menuItem.price && (
            <Text element="span" elementStyle="h5" className="shrink-0">
              {new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
              }).format(menuItem.price)}
            </Text>
          )}
        </div>
        {menuItem.description && (
          <Text className="text-muted-foreground" element="p">
            {menuItem.description}
          </Text>
        )}
        {menuItem.calories && (
          <Text className="text-muted-foreground mt-1" element="span">
            {menuItem.calories} kcal
          </Text>
        )}
        {menuItem.dietaryInformation && (
          <div className="mt-3 flex flex-wrap gap-1 gap-x-2">
            {menuItem.dietaryInformation?.map((info) => (
              <Badge key={info.value} variant={'secondary'}>
                {info.label}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
